import axios from 'axios';

// In local dev (no VITE_API_BASE_URL set — see .env.prod/.env.uat for the
// explicit values used elsewhere), this MUST be a relative path so requests
// go through Vite's dev-server proxy (vite.config.js: '/api' -> localhost:8080)
// and stay same-origin from the browser's point of view. An absolute
// 'http://localhost:8080/api' bypasses that proxy entirely, turning every
// request genuinely cross-origin (port 3000 -> port 8080). The invotrix_auth
// JWT cookie is set via the plain jakarta.servlet Cookie API, which never
// sets SameSite, so it defaults to Lax — and a Lax cookie from a cross-origin
// response is never sent back on the next cross-origin XHR/fetch. That's
// exactly what breaks auth: login succeeds and sets the cookie, but every
// following request (including /auth/me) arrives with no cookie attached
// and 401s, even with withCredentials: true and CORS allow-credentials: true
// on the backend.
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add store context header
api.interceptors.request.use(
  (config) => {
    // Get current store ID from localStorage
    const currentStoreId = localStorage.getItem('currentStoreId');
    
    // Add X-Store-Id header if store is selected
    if (currentStoreId) {
      config.headers['X-Store-Id'] = currentStoreId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Two routine, expected 401s must NOT trigger a hard reload here:
    //  - POST /auth/login with wrong credentials — shown inline via
    //    authSlice's `error` state on the Login page, not a session-expiry.
    //  - GET /auth/me when not logged in — App.jsx calls this on every load
    //    (including while already sitting on /login) to check auth status;
    //    getCurrentUser.rejected + ProtectedRoute's <Navigate> already
    //    redirect gracefully via React Router, no reload needed. Redirecting
    //    here too turns every visit to /login into reload-check-401-reload,
    //    since this same 401 fires again immediately after the "redirect".
    // Instance suspended: every blocked call answers
    // 503 { suspended: true, message } — App.jsx swaps the whole UI for the
    // blocking SuspendedDialog.
    if (error.response?.status === 503 && error.response?.data?.suspended) {
      window.dispatchEvent(new CustomEvent('invotrix:suspended', {
        detail: { reason: error.response.data.message },
      }));
      return Promise.reject(error);
    }

    const url = error.config?.url || '';
    const isAuthCheckRequest = url.includes('/auth/login') || url.includes('/auth/me');

    if (error.response?.status === 401 && !isAuthCheckRequest) {
      // Unauthorized on some other, already-authenticated call (session
      // expired mid-use) - redirect to login.
      localStorage.removeItem('currentStoreId');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;